import { ApiError } from '@/apis'

const DEFAULT_ERROR_MESSAGE = '일시적인 오류가 발생했어요'

/**
 * 에러 객체에서 사용자에게 노출할 메시지를 추출한다.
 *
 * 백엔드 envelope 컨벤션:
 *  - 최상위 `message` = 사용자 노출용 문구 ("상품이 품절 되었습니다." 등)
 *  - `error.description` = 기술적 상세 ("재고가 부족합니다." 등) — 노출 우선순위 낮음
 *
 * 우선순위: envelope.message → error.description → fallback
 *
 * `||` 사용 — null·undefined·빈 문자열("")을 모두 다음 값으로 fallthrough.
 * envelope.message 가 code 와 같으면(message 누락 시 super(code) 로 채워진 경우) 무시.
 */
export function getUserFacingMessage(error: unknown, fallback = DEFAULT_ERROR_MESSAGE): string {
  if (error instanceof ApiError) {
    const envelopeMessage = error.message !== error.code ? error.message : undefined
    return envelopeMessage || error.description || fallback
  }
  return fallback
}

/**
 * 결제 거절(PG 승인 실패) 전용 메시지.
 *
 * PG 거절은 envelope.message 가 "결제에 실패했습니다" 같은 일반 안내이고
 * 실제 사유(잔액부족·한도초과 등)는 error.description("Nicepay API Error: (8323) 잔액부족")
 * 에 담겨오므로, 둘 다 노출한다 — message 를 첫 줄, description 을 둘째 줄로.
 * (사용자가 일반 안내와 구체 사유를 함께 보도록. CardRegisterPage errorMap 과 동일 패턴)
 *
 * 줄 구성: [envelope.message?, error.description?] 중 있는 것만 `\n` 으로 결합.
 *  - message 가 code 와 같으면(누락 시 super(code) 로 채워진 경우) 제외
 *  - description 이 message 와 같으면 중복 제외
 *  - 둘 다 없으면 fallback
 *
 * 노출 컴포넌트(Toast)는 `white-space: pre-line` 으로 `\n` 을 줄바꿈으로 렌더한다.
 */
export function getPaymentFailureMessage(error: unknown, fallback = DEFAULT_ERROR_MESSAGE): string {
  if (error instanceof ApiError) {
    const lines: string[] = []
    const message = error.message !== error.code ? error.message.trim() : ''
    if (message) lines.push(message)
    const description = error.description?.trim()
    if (description && description !== message) lines.push(description)
    return lines.length > 0 ? lines.join('\n') : fallback
  }
  return fallback
}
